import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import type {
  CncCheck,
  CncExportOptions,
  CncExportResult,
  CncFeatureFamily,
  CncMachineProfile,
  CncSeatStandard,
  CncSizeSystem,
} from "../../../shared/api-contract";
import { Disclosure, Icon } from "../../kit";
import { useCaps } from "../../editor/CapsProvider";
import { downloadBlob } from "../../../util/download";
import { useRing } from "../ringContext";
import {
  CNC_SIZE_SYSTEMS,
  cncDialogFileNames,
  cncFileNames,
  cncHeaderLine,
  cncSizeSentence,
  expectedCncSize,
  formatCncDuration,
  grooveShallowSuggestsZFollow,
  headerFieldError,
  machinistNotes,
  programOperations,
  sizeFromFileName,
  splitCheckId,
} from "./cncFormat";
import { loadCncPrefs, saveCncPrefs, validateCncProfile } from "./cncProfile";
import type { CncPrefs } from "./cncProfile";
import { CNC_ZFOLLOW_INPUT_ID, CncProfileEditor } from "./CncProfileEditor";
import { CncBatchSection } from "./CncBatchSection";
import { CncSeatTable } from "./CncSeatTable";
import { derivedSeatRows, enterSeatLabel } from "./cncSeats";
import { useCncProfiles } from "./useCncProfiles";
import type { CncProfilesState } from "./useCncProfiles";
import { Banner, CheckRow, Choice, CncSection, LabeledText, SmallButton } from "./cncUi";

/**
 * CNC program (.NC) — the Ring Builder's 4-axis rotary export (contract v66).
 *
 * A LEFT drawer over the builder panel, not a centred modal: the toolpath
 * preview is drawn in the viewer, and a backdrop would hide the one thing the
 * preview toggle exists to show.
 *
 * How it works: every change (header, size system, families, profile, or the
 * ring itself) re-runs `exportBandCnc` after a short pause. That call IS the
 * check — the viewer plans, posts, re-parses its own text and gates it — so
 * what the dialog shows is always the verdict on the exact text a download
 * would write. Downloads are offered only for the result of the CURRENT inputs
 * (never a stale one while a re-check is in flight), only when the gate passed
 * and the text exists, and the text is saved byte for byte (CRLF kept).
 *
 * The result also carries the `.cam.3dm` (Rhino reference geometry for
 * madCAM). It is not a program, so the viewer issues it even when the gate
 * withholds the program, and the dialog offers it on the same terms.
 *
 * Machine profiles live in the workspace when the user is signed into one,
 * else in this browser (`useCncProfiles`); the profile section says which.
 *
 * ⚠️ Every viewer call here is a v66 method name (`exportBandCncBatch`, in
 * `CncBatchSection`, included). The dialog renders nothing without
 * `caps.ringCncV66`: RPC has no timeout, so calling into an older viewer would
 * hang with nothing on screen to say why.
 */

const FAMILIES: readonly { id: CncFeatureFamily; label: string; hint: string }[] = [
  { id: "stoneRow", label: "Stone row", hint: "Seats cut across the band, the slot through the edges, and a hole under each stone." },
  { id: "seats", label: "Single stone seats", hint: "Seats for stones set one by one on the outside." },
  { id: "grooves", label: "Grooves", hint: "Decorative lines, seams between metals, edge grooves." },
  { id: "slots", label: "Channels and slots", hint: "The channel round the ring and slots that stop short of the edge." },
];
const ALL_FAMILIES = FAMILIES.map((f) => f.id);

/** Quiet period after the last change before re-checking — a slider drag streams states. */
const RECHECK_MS = 450;

interface Checked {
  key: string;
  result: CncExportResult;
}

export function CncExportDialog({ onClose }: { onClose: () => void }) {
  const caps = useCaps();
  if (!caps.ringCncV66) return null;
  return <CncExportDrawer onClose={onClose} />;
}

function CncExportDrawer({ onClose }: { onClose: () => void }) {
  const { state, r } = useRing();

  // ── Remembered inputs ──
  const [prefs, setPrefs] = useState<CncPrefs>(() => loadCncPrefs());
  useEffect(() => saveCncPrefs(prefs), [prefs]);
  const setPref = <K extends keyof CncPrefs>(k: K, v: CncPrefs[K]) => setPrefs((p) => ({ ...p, [k]: v }));

  const [include, setInclude] = useState<CncFeatureFamily[]>(ALL_FAMILIES);
  const [previewOn, setPreviewOn] = useState(false);

  // ── Profiles: the workspace's or this browser's, else the shop's sample ──
  const loadDefault = useCallback(() => r.getCncDefaultProfile(), [r]);
  const profiles = useCncProfiles(loadDefault);
  const { shelf, profileError } = profiles;
  const profile: CncMachineProfile | null = shelf ? shelf.profiles.find((p) => p.name === shelf.selected) ?? shelf.profiles[0] : null;

  /** Replace the profile in use, if it passes — the seat table's one way in (the profile editor keeps its own). */
  const commitProfile = useCallback((next: CncMachineProfile): string[] | null => {
    if (!shelf || !profile) return ["No machine profile is loaded yet."];
    const check = validateCncProfile(next);
    if (!check.ok) return check.errors;
    profiles.setShelf({ selected: check.profile.name, profiles: shelf.profiles.map((p) => (p === profile ? check.profile : p)) });
    return null;
  }, [shelf, profile, profiles]);

  // A `seat.derived` check's "Enter the shop's seat for …": the seat table opens on that row, prefilled.
  const [seatRequest, setSeatRequest] = useState<{ row: CncSeatStandard; key: number } | null>(null);
  const enterSeat = useCallback((row: CncSeatStandard) => setSeatRequest((prev) => ({ row, key: (prev?.key ?? 0) + 1 })), []);
  // A `groove.shallow` check's pointer at the Z-follow switch in the machine profile.
  const showZFollow = useCallback(() => {
    const el = document.getElementById(CNC_ZFOLLOW_INPUT_ID);
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
    el?.focus({ preventScroll: true });
  }, []);

  // ── The ring: any new state makes the last check stale ──
  const [ringStamp, setRingStamp] = useState(0);
  const firstState = useRef(true);
  useEffect(() => {
    if (firstState.current) { firstState.current = false; return; }
    setRingStamp((n) => n + 1);
  }, [state]);

  // ── Header ──
  const modelError = headerFieldError("The model number", prefs.model);
  const labelError = headerFieldError("The label", prefs.label);
  const headerOk = !modelError && !labelError;

  const opts: CncExportOptions | null = useMemo(
    () => (profile && headerOk
      ? { profile, header: { label: prefs.label, model: prefs.model }, sizeSystem: prefs.sizeSystem, include, cam3dm: prefs.cam3dm }
      : null),
    [profile, headerOk, prefs.label, prefs.model, prefs.sizeSystem, include, prefs.cam3dm],
  );
  const [retry, setRetry] = useState(0);
  const key = useMemo(() => (opts ? JSON.stringify({ opts, ringStamp, retry }) : ""), [opts, ringStamp, retry]);

  // ── The check (= the export) ──
  const [checked, setChecked] = useState<Checked | null>(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<{ key: string; message: string } | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current; // also orphans any call still in flight for older inputs
    if (!opts) {
      setRunning(false);
      return;
    }
    setRunning(true);
    const timer = window.setTimeout(() => {
      r.exportBandCnc(opts).then(
        (result) => {
          if (seq.current !== mine) return;
          setChecked({ key, result });
          setRunError(null);
          setRunning(false);
        },
        (e: Error) => {
          if (seq.current !== mine) return;
          setRunError({ key, message: e?.message || "unknown error" });
          setRunning(false);
        },
      );
    }, RECHECK_MS);
    return () => window.clearTimeout(timer);
  }, [key, opts, r]);

  const current = checked && checked.key === key && !running ? checked.result : null;
  const shown = checked?.result ?? null; // last verdict, shown dimmed while re-checking

  // ── Preview overlay ──
  const [previewNote, setPreviewNote] = useState<string | null>(null);
  useEffect(() => {
    if (!previewOn || !opts) return;
    let live = true;
    const timer = window.setTimeout(() => {
      // A design with nothing to cut resolves its report (with `plan.empty`), not null.
      r.previewBandCnc({ ...opts, rapids: prefs.rapids }).then(
        (rep) => live && setPreviewNote(!rep || rep.checks.some((c) => c.id === "plan.empty") ? "Nothing to draw: this design has nothing the program cuts." : null),
        (e: Error) => live && setPreviewNote(`Preview failed: ${e?.message || "unknown error"}`),
      );
    }, RECHECK_MS);
    return () => { live = false; window.clearTimeout(timer); };
  }, [previewOn, opts, key, prefs.rapids, r]);
  // Clear the overlay when the toggle goes off and when the drawer closes.
  useEffect(() => {
    if (previewOn) return;
    setPreviewNote(null);
    void r.previewBandCnc(null).catch(() => undefined);
  }, [previewOn, r]);
  useEffect(() => () => { void r.previewBandCnc(null).catch(() => undefined); }, [r]);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => { if (ev.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // ── Size ──
  const innerMM = state.spec?.innerDiameterMM ?? 0;
  const expected = expectedCncSize(prefs.sizeSystem, { ringSizeUS: state.ringSizeUS, ringSizeEU: state.ringSizeEU, innerDiameterMM: innerMM });
  const actualSize = current ? sizeFromFileName(prefs.model, current.fileName) : null;
  const size = actualSize ?? expected.size;
  // The viewer's note once it has checked (it knows the Indian table), else ours.
  const viewerSizeNote = current?.notes.find((n) => n.startsWith("size: "));
  const sizeSentence = viewerSizeNote ? cncSizeSentence(viewerSizeNote) : expected.note;
  const predicted = headerOk && size ? cncFileNames(prefs.model, size) : null;
  const names = cncDialogFileNames(current, predicted);
  // The footer lists every file a download writes: the .3dm only while its switch is on.
  const fileList = names ? [names.nc, names.airCut, names.setup, ...(prefs.cam3dm && names.cam3dm ? [names.cam3dm] : [])] : [];

  // ── Downloads: current result only, gate passed, text present ──
  const canDownload = !!current && current.ok && typeof current.nc === "string" && current.nc.length > 0;
  const save = (text: string, name: string) =>
    // `endings: "transparent"` (the default, stated) keeps the CRLFs exactly as the viewer wrote them.
    downloadBlob(new Blob([text], { type: "text/plain", endings: "transparent" }), name);
  // The .3dm is reference geometry, issued whatever the gate said — so it is
  // offered for the current result on its own terms, not behind `canDownload`.
  const cam3dm = current?.cam3dm && current.cam3dmFileName ? { bytes: current.cam3dm, name: current.cam3dmFileName } : null;
  const cam3dmWhy = cam3dmAbsence(current, opts, running);

  const calibrated = profile?.calibrated ?? false;

  return createPortal(
    <div
      role="dialog"
      aria-modal="false"
      aria-label="CNC program"
      data-testid="cnc-dialog"
      className="fixed inset-y-0 left-0 z-[60] flex w-[480px] max-w-[100vw] flex-col border-r border-ed-border bg-ed-panel text-ed-text shadow-[12px_0_40px_rgba(0,0,0,0.12)]"
    >
      {/* ── Title ── */}
      <header className="flex shrink-0 items-start justify-between gap-3 border-b border-ed-border px-5 py-4">
        <div className="flex flex-col gap-0.5">
          <h2 className="font-display text-[16px] font-semibold text-ed-text">CNC program (.NC)</h2>
          <p className="font-body text-[12px] text-ed-text-2">The band as a program for the shop&rsquo;s 4-axis machine.</p>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-ed-text-2 transition-colors hover:bg-ed-hover"
        >
          <Icon name="close" size={18} />
        </button>
      </header>

      <div className="scroll-thin flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
        {profile && !calibrated ? (
          <Banner tone="warn" icon="warning" testId="cnc-uncalibrated">
            <span className="font-semibold">Not calibrated.</span> This machine profile has not been proven at the shop.
            Run the air-cut file first, then a direction test and a brass test piece, before cutting metal. The setup
            sheet is stamped UNCALIBRATED.
          </Banner>
        ) : null}
        {profileError && !shelf ? <Banner tone="fail" icon="error">{profileError}</Banner> : null}

        {/* ── Header ── */}
        <CncSection title="Program details">
          <div className="flex gap-3">
            <LabeledText testId="cnc-model" label="Model number" value={prefs.model} placeholder="e.g. 368222" mono
              onChange={(v) => setPref("model", v.trim())} error={prefs.model ? modelError : null} />
            <LabeledText testId="cnc-label" label="Label" value={prefs.label} placeholder="e.g. UGTH2310" mono
              onChange={(v) => setPref("label", v.trim())} error={prefs.label ? labelError : null} />
          </div>
          {!headerOk ? (
            <span className="font-body text-[11px] text-ed-text-3">Enter a model number and a label to check the program.</span>
          ) : profile && size ? (
            <div className="flex flex-col gap-1 rounded-md bg-ed-panel-2 px-3 py-2">
              <span className="font-body text-[11px] text-ed-text-3">First line of the program</span>
              <code data-testid="cnc-header-line" className="whitespace-pre font-mono text-[12px] text-ed-text">
                {cncHeaderLine(profile.framing.header, { label: prefs.label, size, model: prefs.model })}
              </code>
            </div>
          ) : null}
        </CncSection>

        {/* ── Size ── */}
        <CncSection title="Ring size">
          <div className="flex items-baseline justify-between gap-2">
            <span data-testid="cnc-size" className="font-body text-[13px] tabular-nums text-ed-text">
              {size ? `${prefs.sizeSystem === "IN" ? "India" : prefs.sizeSystem} ${size}` : "Worked out when checked"}
              {innerMM > 0 ? <span className="text-ed-text-3"> · Ø {innerMM.toFixed(2)} mm inside</span> : null}
            </span>
          </div>
          <Choice<CncSizeSystem> testId="cnc-size-system" options={CNC_SIZE_SYSTEMS} value={prefs.sizeSystem} onChange={(v) => setPref("sizeSystem", v)} />
          <span data-testid="cnc-size-note" className="font-body text-[11px] text-ed-text-3">
            Which size scale the shop reads the program&rsquo;s size in. US is usual for quarter sizes like 10.75.
            {sizeSentence ? ` ${sizeSentence}` : ""}
          </span>
        </CncSection>

        {/* ── Features ── */}
        <CncSection title="What gets cut">
          <div className="flex flex-col gap-2">
            {FAMILIES.map((f) => (
              <CheckRow
                key={f.id}
                testId={`cnc-family-${f.id}`}
                label={f.label}
                hint={f.hint}
                checked={include.includes(f.id)}
                onChange={(on) => setInclude((cur) => (on ? ALL_FAMILIES.filter((x) => x === f.id || cur.includes(x)) : cur.filter((x) => x !== f.id)))}
              />
            ))}
          </div>
          <Operations result={shown} stale={!current} />
          {shown && shown.notMachined.length > 0 ? (
            <div className="flex flex-col gap-1.5" data-testid="cnc-not-machined">
              <span className="font-body text-[12px] font-semibold text-ed-text">Not cut by this program</span>
              <ul className="flex flex-col gap-1.5">
                {shown.notMachined.map((n, i) => (
                  <li key={`${n.feature}-${i}`} className="rounded-md border border-ed-border px-3 py-2 font-body text-[12px]">
                    <span className="font-medium text-ed-text">{n.feature}</span>
                    <span className="text-ed-text-2"> — {n.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </CncSection>

        {/* ── Checks ── */}
        <CncSection
          title="Checks"
          aside={running && opts ? <span className="font-body text-[11px] text-ed-text-3">Checking…</span> : null}
        >
          {runError && runError.key === key ? (
            <Banner tone="fail" icon="error" testId="cnc-run-error">
              Could not make the program: {runError.message}
              <div className="mt-2"><SmallButton onClick={() => setRetry((n) => n + 1)}>Try again</SmallButton></div>
            </Banner>
          ) : null}
          {shown ? (
            <Report result={shown} stale={!current} onEnterSeat={enterSeat}
              onShowZFollow={profile && !profile.kinematics.allowSimultaneousZ ? showZFollow : undefined} />
          ) : opts ? (
            <span className="font-body text-[12px] text-ed-text-3">Checking the program…</span>
          ) : (
            <span className="font-body text-[12px] text-ed-text-3">Nothing checked yet.</span>
          )}
        </CncSection>

        {/* ── Preview ── */}
        <CncSection title="Preview on the ring">
          <CheckRow testId="cnc-preview" checked={previewOn} onChange={setPreviewOn} disabled={!opts}
            label="Show the cutter paths on the ring" hint="Cutting moves as bright blue lines, edged dark so they read on any metal." />
          <CheckRow checked={prefs.rapids} onChange={(v) => setPref("rapids", v)} disabled={!previewOn}
            label="Also show moves in the air" hint="Fast moves between cuts, dashed slate." />
          {previewOn && previewNote ? <span className="font-body text-[11px] text-ed-text-3">{previewNote}</span> : null}
        </CncSection>

        {/* ── Sizes ── */}
        <CncBatchSection opts={opts} system={prefs.sizeSystem} ringStamp={ringStamp} ringSizeUS={state.ringSizeUS} profile={profile} />

        {/* ── Stone seats (the profile's seat table) ── */}
        {profile ? (
          <CncSeatTable profile={profile} onCommit={commitProfile} request={seatRequest} storeNote={seatStoreNote(profiles, profile.name)} />
        ) : null}

        {/* ── Profile ── */}
        <CncSection title="Machine profile" aside={<StoreLabel profiles={profiles} />}>
          <ProfileStoreNotes profiles={profiles} />
          {shelf ? (
            <CncProfileEditor shelf={shelf} onShelfChange={profiles.setShelf} loadDefault={loadDefault} />
          ) : profiles.loading ? (
            <span className="font-body text-[12px] text-ed-text-3">Loading the machine profiles…</span>
          ) : null}
        </CncSection>
      </div>

      {/* ── Downloads ── */}
      <footer className="flex shrink-0 flex-col gap-2 border-t border-ed-border bg-ed-panel px-5 py-3">
        {!canDownload ? (
          <span data-testid="cnc-download-why" className="font-body text-[11px] text-ed-text-3">
            {!opts
              ? "Downloads appear once the program has been checked."
              : runError && runError.key === key && !running
                ? "No program: the viewer could not make one (see Checks)."
                : !current
                ? "Checking — downloads unlock when the check finishes."
                : "No program to download: it did not pass the checks above."}
          </span>
        ) : null}
        <div className="grid grid-cols-3 gap-2">
          <SmallButton testId="cnc-download-nc" tone="ink" disabled={!canDownload}
            title={names?.nc} onClick={() => current?.nc && save(current.nc, current.fileName)}>
            <Icon name="download" size={14} /> Program
          </SmallButton>
          <SmallButton testId="cnc-download-aircut" disabled={!canDownload || !current?.airCut}
            title={names?.airCut} onClick={() => current?.airCut && save(current.airCut, current.airCutFileName)}>
            <Icon name="download" size={14} /> Air cut
          </SmallButton>
          <SmallButton testId="cnc-download-setup" disabled={!canDownload}
            title={names?.setup} onClick={() => current && save(current.setupSheet, current.setupSheetFileName)}>
            <Icon name="download" size={14} /> Setup sheet
          </SmallButton>
        </div>
        <SmallButton testId="cnc-download-3dm" disabled={!cam3dm}
          title={cam3dm?.name ?? cam3dmWhy ?? undefined}
          onClick={() => cam3dm && downloadBlob(new Blob([new Uint8Array(cam3dm.bytes)], { type: "application/octet-stream" }), cam3dm.name)}>
          <Icon name="deployed_code" size={14} /> .3dm for Rhino / madCAM
        </SmallButton>
        <span data-testid="cnc-3dm-why" className="font-body text-[11px] text-ed-text-3">
          {cam3dm
            ? current?.ok
              ? "Not a program: the blank, every cut and the cutter paths, to program the part in madCAM."
              : "Not a program. The plan was refused, so it holds only the blank, where each refused cut was planned (no cutter paths) and a GATE FAILED mark naming the checks that failed."
            : cam3dmWhy}
        </span>
        <CheckRow testId="cnc-3dm-toggle" checked={prefs.cam3dm} onChange={(v) => setPref("cam3dm", v)}
          label="Make the .3dm with each check" hint="Off: the viewer skips loading its Rhino file writer from the internet — the program is not affected either way." />
        {fileList.length ? (
          // Wrapped, never truncated: every file a download writes is named in
          // full (at 1600 px the single ellipsised line cut the .cam.3dm name off).
          <ul data-testid="cnc-file-names" className="flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[11px] text-ed-text-3" title={fileList.join(" · ")}>
            {fileList.map((name) => (
              <li key={name} className="min-w-0 [overflow-wrap:anywhere]">{name}</li>
            ))}
          </ul>
        ) : null}
      </footer>
    </div>,
    document.body,
  );
}

/** What the program cuts, read off its own text (`programOperations`). */
function Operations({ result, stale }: { result: CncExportResult | null; stale: boolean }) {
  if (!result) return null;
  if (!result.nc) {
    return <span className="font-body text-[12px] text-ed-text-3">No program was made, so nothing will be cut — see the checks below.</span>;
  }
  const ops = programOperations(result.nc);
  return (
    <div className={`flex flex-col gap-1.5 ${stale ? "opacity-50" : ""}`} data-testid="cnc-operations">
      <span className="font-body text-[12px] font-semibold text-ed-text">Cut by this program, in order</span>
      {ops.length === 0 ? (
        <span className="font-body text-[12px] text-ed-text-3">The program names no operations.</span>
      ) : (
        <ol className="flex flex-col gap-1">
          {ops.map((op, i) => (
            <li key={i} className="flex items-baseline gap-2 font-mono text-[11px] text-ed-text">
              <span className="w-5 shrink-0 text-right tabular-nums text-ed-text-3">{i + 1}.</span>
              <span className="whitespace-pre-wrap">{op.text}</span>
              {op.count > 1 ? <span className="shrink-0 font-body text-ed-text-3">× {op.count}</span> : null}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * The gate's verdict: fails first (red, blocking), then warns (amber), passes
 * folded away. Two checks carry their fix as a button: `seat.derived` opens
 * the seat table on each guessed row, prefilled, and `groove.shallow` points
 * at the Z-follow switch — while it is off, and only when the warn itself
 * names Z-follow as a fix (a shortfall across the face of a crowned blank).
 */
function Report({ result, stale, onEnterSeat, onShowZFollow }: {
  result: CncExportResult;
  stale: boolean;
  onEnterSeat: (row: CncSeatStandard) => void;
  onShowZFollow?: () => void;
}) {
  const checks = result.report.checks;
  // The air cut's own fails also withhold the download; show them, labelled.
  const airFails = result.airCutReport.checks.filter((c) => c.level === "fail");
  const fails = checks.filter((c) => c.level === "fail");
  const warns = checks.filter((c) => c.level === "warn");
  const passes = checks.filter((c) => c.level === "pass");
  const t = result.report.cycleTimeMin;
  // Every warn check's message is also in `warnings` (for the setup sheet); list each once.
  const notes = machinistNotes(result.warnings, warns);
  const guesses = derivedSeatRows(result);
  const action = (c: CncCheck): ReactNode => {
    if (c.id === "seat.derived" && c.level === "fail" && guesses.length) {
      return guesses.map((row) => (
        <SmallButton key={`${row.cut ?? "round"}-${row.stoneMM}`} testId="cnc-enter-seat" onClick={() => onEnterSeat(row)}>
          <Icon name="edit_note" size={14} /> {enterSeatLabel(row)}
        </SmallButton>
      ));
    }
    if (c.id === "groove.shallow" && onShowZFollow && grooveShallowSuggestsZFollow(c.message)) {
      return (
        <SmallButton testId="cnc-show-zfollow" onClick={onShowZFollow}>
          <Icon name="tune" size={14} /> Show the Z-follow switch
        </SmallButton>
      );
    }
    return null;
  };

  return (
    <div className={`flex flex-col gap-3 ${stale ? "opacity-50" : ""}`} data-testid="cnc-report">
      {result.ok ? (
        <Banner tone="info" icon="check_circle" testId="cnc-report-ok">
          The program passed every check{warns.length ? `, with ${warns.length} thing${warns.length === 1 ? "" : "s"} to read below` : ""}.
        </Banner>
      ) : (
        <Banner tone="fail" icon="block" testId="cnc-report-fail">
          <span className="font-semibold">The program is withheld.</span> It failed {fails.length + airFails.length} check
          {fails.length + airFails.length === 1 ? "" : "s"}; nothing can be downloaded until the design or the profile changes so they pass.
        </Banner>
      )}

      {fails.length + airFails.length > 0 ? (
        // The air cut repeats the program's own fails (seat.derived among them): the fix is offered once, on the program's.
        <CheckList tone="fail" title="Failed" checks={[...fails, ...airFails.map((c) => ({ ...c, message: `Air cut: ${c.message}` }))]}
          action={(c) => (fails.includes(c) ? action(c) : null)} />
      ) : null}
      {warns.length > 0 ? <CheckList tone="warn" title="Read before running" checks={warns} action={action} /> : null}
      {notes.length > 0 ? (
        <div className="flex flex-col gap-1.5" data-testid="cnc-machinist-notes">
          <span className="font-body text-[12px] font-semibold text-amber-900">Notes for the machinist</span>
          <ul className="flex flex-col gap-1">
            {notes.map((w, i) => (
              <li key={i} className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 font-body text-[12px] text-amber-900">
                <Message text={w} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {result.nc ? (
        <span data-testid="cnc-cycle-time" className="font-body text-[12px] text-ed-text-2">
          Cutting time about {formatCncDuration(t.combined)}, or {formatCncDuration(t.linearOnly)} if the machine feeds rotation
          on its own{t.cannedCycle > 0 ? `, plus ${formatCncDuration(t.cannedCycle)} drilling` : ""}. Fast moves are not counted.
        </span>
      ) : null}

      {passes.length > 0 ? (
        <Disclosure label={`${passes.length} checks passed`}>
          <ul className="flex flex-col gap-1">
            {passes.map((c, i) => <CheckItem key={`${c.id}-${i}`} check={c} tone="pass" />)}
          </ul>
        </Disclosure>
      ) : null}
      {result.notes.length > 0 ? (
        <Disclosure label={`Planning notes (${result.notes.length})`}>
          <ul className="flex flex-col gap-1">
            {result.notes.map((n, i) => <li key={i} className="font-body text-[11px] text-ed-text-2"><Message text={n} /></li>)}
          </ul>
        </Disclosure>
      ) : null}
      <span className="font-mono text-[11px] text-ed-text-3">{result.generatorVersion} · design {result.designHash}</span>
    </div>
  );
}

function CheckList({ tone, title, checks, action }: {
  tone: "fail" | "warn";
  title: string;
  checks: CncCheck[];
  /** What a check offers to fix it, if anything. */
  action?: (c: CncCheck) => ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5" data-testid={`cnc-checks-${tone}`}>
      <span className={`font-body text-[12px] font-semibold ${tone === "fail" ? "text-ed-red" : "text-amber-900"}`}>{title}</span>
      <ul className="flex flex-col gap-1">
        {checks.map((c, i) => <CheckItem key={`${c.id}-${i}`} check={c} tone={tone} action={action?.(c)} />)}
      </ul>
    </div>
  );
}

function CheckItem({ check, tone, action }: { check: CncCheck; tone: "fail" | "warn" | "pass"; action?: ReactNode }) {
  const cls = {
    fail: "border-red-300 bg-red-50 text-ed-red",
    warn: "border-amber-300 bg-amber-50 text-amber-900",
    pass: "border-ed-border bg-ed-panel text-ed-text-2",
  }[tone];
  return (
    <li className={`flex flex-col gap-0.5 rounded-md border px-3 py-1.5 font-body text-[12px] ${cls}`} title={check.id}>
      <span>{check.message}</span>
      {action ? <span className="flex flex-wrap gap-1.5 py-1">{action}</span> : null}
      {/* The id is for whoever matches this against the setup sheet: last, small, never the opening words. */}
      <span className="font-mono text-[11px] opacity-60">
        {check.line !== undefined ? `line ${check.line} · ` : ""}{check.id}
      </span>
    </li>
  );
}

/** A message with any check id it opens with moved to a small trailing tag (and the tooltip). */
function Message({ text }: { text: string }) {
  const { id, message } = splitCheckId(text);
  if (!id) return <>{text}</>;
  return (
    <span title={id}>
      {message} <span className="font-mono text-[11px] opacity-60">{id}</span>
    </span>
  );
}

/** Why there is no .3dm to offer, or null when there is one. */
function cam3dmAbsence(current: CncExportResult | null, opts: CncExportOptions | null, running: boolean): string | null {
  if (current?.cam3dm) return null;
  if (opts?.cam3dm === false) return "The .3dm is switched off below.";
  if (!opts) return "The .3dm appears once the program has been checked.";
  if (running || !current) return "Checking — the .3dm comes with the check.";
  const note = current.notes.find((n) => n.startsWith("cam3dm."));
  if (note) {
    const { id, message } = splitCheckId(note);
    return id === "cam3dm.unavailable"
      ? "No .3dm this time: the viewer could not load its Rhino file writer (rhino3dm, from the internet) in time. The program is not affected — try again when online, or switch the .3dm off below."
      : `No .3dm this time: ${message}`;
  }
  return "No .3dm came back with this check.";
}

/** Where a seat-table edit goes, in a few words beside the "Stone seats" title. */
function seatStoreNote(profiles: CncProfilesState, name: string): string {
  if (profiles.kind === "browser") return `Saved in “${name}”, in this browser`;
  if (profiles.storedUnusable || !profiles.canEdit) return "For this export only — not saved";
  return `Saved in “${name}” for everyone in ${profiles.workspaceName ?? "the workspace"}`;
}

/** Which store the profiles are in, in a few words, beside the section title. */
function StoreLabel({ profiles }: { profiles: CncProfilesState }) {
  const text = profiles.loading
    ? "Loading…"
    : profiles.kind === "workspace"
      ? profiles.saving
        ? "Saving to the workspace…"
        : profiles.unsaved && profiles.canEdit && !profiles.error
          ? "Not saved yet"
          : `Shared in ${profiles.workspaceName ?? "your workspace"}`
      : "Saved in this browser";
  return <span data-testid="cnc-profile-store" className="font-body text-[11px] text-ed-text-3">{text}</span>;
}

/**
 * The workspace has saved profiles, but none passes today's checks. Not
 * "empty": saving over them deletes them, so that takes a second, explicit
 * click, and until then edits here are for this export only.
 */
function StoredUnusableBanner({ profiles, unusable }: { profiles: CncProfilesState; unusable: { as: "browser" | "sample" } }) {
  const [confirming, setConfirming] = useState(false);
  const n = profiles.left.length;
  const these = unusable.as === "browser" ? "the profiles saved in this browser" : "the shop's sample profile";
  return (
    <Banner tone="warn" icon="warning" testId="cnc-profile-stored-unusable">
      {n === 1 ? "The workspace's saved profile does" : `None of the workspace's ${n} saved profiles`}{n === 1 ? " not" : ""} pass today&rsquo;s checks (listed below), so this export uses {these}. Changes here are for this export only and are not saved.
      {profiles.canEdit ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {confirming ? (
            <>
              <SmallButton testId="cnc-profile-replace-confirm" tone="danger" disabled={profiles.saving} onClick={() => { setConfirming(false); profiles.replaceStored(); }}>
                Delete the saved {n === 1 ? "profile" : `${n} profiles`} and save these
              </SmallButton>
              <SmallButton onClick={() => setConfirming(false)}>Cancel</SmallButton>
            </>
          ) : (
            <SmallButton testId="cnc-profile-replace" onClick={() => setConfirming(true)}>Replace the saved profiles with these…</SmallButton>
          )}
        </div>
      ) : null}
    </Banner>
  );
}

/** What the store in use means for this user: who sees an edit, whether it is saved, and anything left out. */
function ProfileStoreNotes({ profiles }: { profiles: CncProfilesState }) {
  if (profiles.loading) return null;
  const selected = profiles.shelf?.selected ?? null;
  if (profiles.kind === "browser") {
    return (
      <div className="flex flex-col gap-1.5">
        {profiles.fallback ? <Banner tone="warn" icon="cloud_off">{profiles.fallback}</Banner> : null}
        <span className="font-body text-[11px] text-ed-text-3">
          Profiles are kept in this browser only{profiles.fallback ? " until the workspace can be reached" : ""}: another computer will not see them.
        </span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5" data-testid="cnc-profile-workspace">
      <span className="font-body text-[11px] text-ed-text-3">
        Shared with everyone in {profiles.workspaceName ?? "the workspace"}.
        {profiles.canEdit
          ? " Changes are saved for all of them."
          : " Only the owner, admins and managers can change them; a change you make here is used for this export only."}
        {profiles.workspaceDefault ? ` Everyone opens on “${profiles.workspaceDefault}”.` : ""}
      </span>
      {profiles.canEdit && selected && profiles.workspaceDefault !== selected && profiles.shelf && profiles.shelf.profiles.length > 1 ? (
        <div><SmallButton testId="cnc-profile-make-default" onClick={profiles.makeDefault}>Make “{selected}” everyone&rsquo;s default</SmallButton></div>
      ) : null}
      {profiles.seeded ? (
        <Banner tone="info" icon="cloud_upload" testId="cnc-profile-seeded">
          The workspace has no saved profiles yet, so{" "}
          {profiles.seeded === "browser" ? "these are the ones saved in this browser" : "this is the shop's sample profile"}.
          {profiles.canEdit ? (
            <div className="mt-2">
              <SmallButton testId="cnc-profile-save-workspace" disabled={profiles.saving} onClick={profiles.saveNow}>
                Save {profiles.seeded === "browser" ? "them" : "it"} to the workspace
              </SmallButton>
            </div>
          ) : null}
        </Banner>
      ) : null}
      {profiles.storedUnusable ? <StoredUnusableBanner profiles={profiles} unusable={profiles.storedUnusable} /> : null}
      {profiles.error ? (
        <Banner tone="fail" icon="error" testId="cnc-profile-save-error">
          {profiles.error}
          <div className="mt-2 flex gap-2">
            {profiles.canEdit ? <SmallButton onClick={profiles.saveNow}>Try saving again</SmallButton> : null}
            <SmallButton onClick={profiles.reload}>Reload the workspace&rsquo;s profiles</SmallButton>
          </div>
        </Banner>
      ) : null}
      {profiles.left.length > 0 ? (
        <Banner tone="warn" icon="warning" testId="cnc-profile-left-out">
          {profiles.left.length === 1 ? "One saved profile is" : `${profiles.left.length} saved profiles are`} left out because{" "}
          {profiles.left.length === 1 ? "it no longer passes" : "they no longer pass"} the checks:
          <ul className="mt-1 list-disc pl-4">
            {profiles.left.map((l) => <li key={l.name}>{l.name}: {l.errors[0] ?? "invalid"}</li>)}
          </ul>
          {profiles.canEdit ? "The next save of the workspace's list removes them; fix one in its JSON first to keep it." : null}
        </Banner>
      ) : null}
    </div>
  );
}
